import type { Ball } from '@game/models/Ball';
import { BALL_MIN_VELOCITY } from '../config/PhysicsConfig';

/**
 * BallLauncher calculates ball velocity based on user input
 */
export class BallLauncher {
  /**
   * Calculate launch velocity from aim angle and power
   * 
   * @param angle - Angle in radians (0 = straight down lane, -π/4 to π/4 for left/right)
   * @param power - Power as percentage (0.0 to 1.0)
   * @param ballConfig - Ball configuration
   * @returns Velocity vector { x, y }
   */
  calculateVelocity(
    angle: number,
    power: number,
    ballConfig: Ball['config']
  ): { x: number; y: number } {
    // Clamp power to valid range
    const clampedPower = Math.max(0, Math.min(1, power));
    
    // Calculate speed: interpolate between min and max velocity
    const speed = BALL_MIN_VELOCITY + (ballConfig.maxVelocity - BALL_MIN_VELOCITY) * clampedPower;
    
    // Convert angle to velocity components
    const vx = Math.sin(angle) * speed;
    const vy = Math.cos(angle) * speed; // Positive Y is forward down the lane
    
    return { x: vx, y: vy };
  }

  /**
   * Calculate angle from screen coordinates (e.g., mouse position)
   * 
   * @param startX - Ball start X position
   * @param startY - Ball start Y position
   * @param targetX - Target X position (where user is aiming)
   * @param targetY - Target Y position
   * @returns Angle in radians
   */
  calculateAngleFromTarget(
    startX: number,
    startY: number,
    targetX: number,
    targetY: number
  ): number {
    const dx = targetX - startX;
    const dy = targetY - startY;
    
    return Math.atan2(dx, dy); // atan2 gives angle from vertical
  }

  /**
   * Calculate power from distance (e.g., drag distance)
   * 
   * @param distance - Distance in meters or pixels
   * @param maxDistance - Maximum distance for full power
   * @returns Power percentage (0.0 to 1.0)
   */
  calculatePowerFromDistance(distance: number, maxDistance: number): number {
    const power = distance / maxDistance;
    return Math.max(0, Math.min(1, power));
  }

  /**
   * Validate launch parameters and clamp to valid ranges
   */
  validateLaunchParameters(angle: number, power: number): {
    angle: number;
    power: number;
    isValid: boolean;
  } {
    const maxAngle = Math.PI / 4; // 45 degrees max left/right
    const clampedAngle = Math.max(-maxAngle, Math.min(maxAngle, angle));
    const clampedPower = Math.max(0, Math.min(1, power));
    
    return {
      angle: clampedAngle,
      power: clampedPower,
      isValid: angle === clampedAngle && power === clampedPower,
    };
  }
}
